import React, { useRef, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Placeholder: Imagem de garfo e faca cruzados (idêntica à referência do usuário) - ~1.7KB
const FOOD_PLACEHOLDER_IMG = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QBMRXhpZgAATU0AKgAAAAgABAEAAAQAAAABAAAEOAEBAAQAAAABAAAEQAESAAMAAAABAAEAAIdpAAQAAAABAAAAPgAAAAAAAAAAAAD/4gIoSUNDX1BST0ZJTEUAAQEAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANv/bAEMADQkKCwoIDQsKCw4ODQ8TIBUTEhITJxweFyAuKTEwLiktLDM6Sj4zNkY3LC1AV0FGTE5SU1IyPlphWlBgSlFST//bAEMBDg4OExETJhUVJk81LTVPT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT//AABEIAGQAYwMBIgACEQEDEQH/xAAZAAEBAQEBAQAAAAAAAAAAAAAAAQQDAgb/xAApEAEAAgIABQMEAgMAAAAAAAAAAQIDEQQSITFBIlFhEzJCcVLhgaHB/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAH/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwD7BREVRFAEUAAARQBFBNCgAICg90pvrbtreo769weB2vj5utdc2t6jtaPeHARQBQa+Hwa1e8dfEezLad2tMdpkR5UgFBAFEAV0xW/GZ1EzuJ/jLmCNMRMzrUxO+sR+M+8fBnw+j6nTmj7tdpMGesRrJ0mI1Fvh2tMZcd647RM6UYGrh8GvXeOviHnhsUTaZv3rP2+zTe9cdea0/wBg8cRk5MU6726QwQ95Mk5Lza3+I9ngFARQQBUVAURQGjBhyRX6lZ1PiJ8rw/D79d46eIOKzd8dJ/c/8VHTpmrGTHPLeOn9Sx35+eeffNHfb1hyTjtuO3mGvJjrnpFqz18SDCkLas1tNbRqYRFURQBAFAAdeGrW2WItr4/bkA28Rm5I5az6p/0xAqDrw+b6dtT9s9/hyRFbeKik4ubcb8T7sSoIKAoIAoAAAAAIoAIoAAAAIACgAIoAIAAAKAAAD//Z";

// Componente de imagem com placeholder de garfo/faca + fade-in suave
const LazyGridImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const imageUrlRef = useRef(src);
  
  useEffect(() => {
    if (imageUrlRef.current !== src) {
      imageUrlRef.current = src;
      setIsLoaded(false);
      setHasError(false);
    }
  }, [src]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px' }
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const fallbackSrc = "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=200&fit=crop&crop=center";
  
  return (
    <div ref={imgRef} className={`relative bg-gray-100 overflow-hidden ${className}`}>
      {/* Placeholder: Ícone de garfo/faca */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${isLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundColor: '#f3f4f6' }}
      >
        <img 
          src={FOOD_PLACEHOLDER_IMG} 
          alt="" 
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Imagem HD com fade-in suave */}
      <img
        src={isInView ? (hasError ? fallbackSrc : src) : undefined}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-500 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => requestAnimationFrame(() => setIsLoaded(true))}
        onError={() => {
          setHasError(true);
          setIsLoaded(true);
        }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
};

export default function ProductGridView({ categories, onProductClick }) {
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {categories.map(category => (
        <CategoryCarousel 
          key={category.id}
          category={category}
          onProductClick={onProductClick}
        />
      ))}
    </div>
  );
}

function CategoryCarousel({ category, onProductClick }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 2,
    containScroll: 'trimSnaps',
    dragFree: true
  });

  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  if (!category.products || category.products.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Category Header with Navigation */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
        
        {/* Navigation Buttons - Desktop Only */}
        <div className="hidden sm:flex gap-2">
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={`p-1.5 rounded-full transition-all ${
              canScrollPrev
                ? 'bg-white shadow hover:shadow-md'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            style={{ color: canScrollPrev ? 'var(--color-primary, #3B82F6)' : undefined }}
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={`p-1.5 rounded-full transition-all ${
              canScrollNext
                ? 'bg-white shadow hover:shadow-md'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            style={{ color: canScrollNext ? 'var(--color-primary, #3B82F6)' : undefined }}
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="overflow-hidden -mx-1" ref={emblaRef}>
        <div className="flex gap-3">
          {category.products.map((product, index) => (
            <div
              key={product.id}
              className="flex-none w-[160px] sm:w-[180px] lg:w-[190px]"
            >
              <ProductCard 
                product={product} 
                onClick={() => onProductClick(product)}
                index={index}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Swipe Hint */}
      {category.products.length > 2 && (
        <div className="text-center text-xs text-gray-400 mt-2 sm:hidden">
          ← Arraste para ver mais →
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onClick, index }) {
  // Badges baseados no índice (mock)
  const badges = [];
  if (index === 0) badges.push({ text: 'Mais vendido', color: 'bg-red-500', icon: '🔥' });
  if (index === 2) badges.push({ text: 'Oferta', color: 'bg-yellow-500', icon: '⚡' });
  if (index === 4) badges.push({ text: 'Novidade', color: 'bg-green-500', icon: '🆕' });

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 group"
    >
      {/* Product Image with Lazy Loading */}
      <div className="relative h-[140px] sm:h-[150px] bg-gray-100 overflow-hidden">
        <LazyGridImage
          src={product.image_url}
          alt={product.name}
          className="w-full h-full transition-transform duration-300 group-hover:scale-110"
        />
        
        {/* Badge (se houver) */}
        {badges.length > 0 && (
          <div className="absolute top-2 left-2">
            <div className={`${badges[0].color} text-white px-2 py-0.5 rounded text-xs font-bold shadow-md flex items-center gap-1`}>
              <span>{badges[0].icon}</span>
              <span className="hidden sm:inline">{badges[0].text}</span>
            </div>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-3">
        {/* Product Name */}
        <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 min-h-[32px] leading-tight">
          {product.name}
        </h3>
        
        {/* Product Description */}
        {product.description && (
          <p className="text-xs text-gray-500 mb-2 line-clamp-2 min-h-[28px]">
            {product.description}
          </p>
        )}
        
        {/* Price */}
        <div>
          <div className="text-lg font-bold" style={{ color: 'var(--color-primary, #3B82F6)' }}>
            R$ {product.price.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}
